import React, {useEffect, useMemo, useRef, useState} from "react"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faSearch, faTimes} from "@fortawesome/free-solid-svg-icons"

import styles from "./Search.module.scss"
import getSearchIndex from "../../utils/searchIndex"
import searchPages from "../../utils/searchPages"

export default function Search({onSelect}) {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const containerRef = useRef(null)
    const inputRef = useRef(null)

    const results = useMemo(() => {
        if (!isOpen || !query.trim()) return []
        return searchPages(getSearchIndex(), query)
    }, [isOpen, query])

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                closeSearch()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    function openSearch() {
        setIsOpen(true)
        setTimeout(() => inputRef.current?.focus(), 0)
    }

    function closeSearch() {
        setIsOpen(false)
        setQuery('')
    }

    function handleSelect(result) {
        onSelect(result)
        closeSearch()
    }

    function handleKeyDown(event) {
        if (event.key === 'Escape') {
            closeSearch()
        } else if (event.key === 'Enter' && results.length > 0) {
            handleSelect(results[0])
        }
    }

    return (
        <div className={styles.search} ref={containerRef}>
            <FontAwesomeIcon
                icon={isOpen ? faTimes : faSearch}
                onClick={isOpen ? closeSearch : openSearch}
                title="Search"
                className={styles.icon}
            />
            {isOpen && (
                <div className={styles.panel}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search pages & tools…"
                        className={styles.input}
                    />
                    {results.length > 0 && (
                        <ul className={styles.results}>
                            {results.map((result) => (
                                <li key={result.to} onClick={() => handleSelect(result)}>
                                    <span className={styles.resultTitle}>{result.title}</span>
                                    {result.snippet && <span className={styles.resultSnippet}>{result.snippet}</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                    {query.trim() && results.length === 0 && (
                        <div className={styles.noResults}>No matches</div>
                    )}
                </div>
            )}
        </div>
    )
}
